import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'react-toastify'

import {
    useAssignTechnicianToOrderMutation,
    useLazyGetDispatcherTechniciansQuery,
    useReassignTechnicianToOrderMutation,
} from '@/services/dispatcher/dispather'
import type { DispatcherUnassignment } from '@/types/dispatcher/dashboard.types'

import Button from '../common/Button'
import Modal from '../common/Modal'
import SelectBoxAsynk from '../common/SelectBoxAsynk'

interface AssignOrderModalProps {
    isOpen: boolean
    order: DispatcherUnassignment
    onClose: () => void
    onSubmit?: () => void
}

const AssignOrderModal = ({
    isOpen,
    order,
    onClose,
    onSubmit,
}: AssignOrderModalProps) => {
    const [assignTechnicianToOrder, { isLoading: isAssigning }] =
        useAssignTechnicianToOrderMutation()
    const [reassignTechnicianToOrder, { isLoading: isReassigning }] =
        useReassignTechnicianToOrderMutation()
    const [technician, setTechnician] = useState<{
        value: number
        label: string
    } | null>(null)
    const [getTechnicians] = useLazyGetDispatcherTechniciansQuery()

    const assign = async (technician: { value: number; label: string }) => {
        const response = await assignTechnicianToOrder({
            orderId: order.id,
            body: { id: technician.value },
        }).unwrap()
        if (response.success) {
            toast.success('Order assigned successfully.')
            onClose()
            onSubmit?.()
        } else {
            toast.error(response.message || 'Failed to assign order.')
        }
    }
    const reassign = async (technician: { value: number; label: string }) => {
        const response = await reassignTechnicianToOrder({
            orderId: order.id,
            body: { id: technician.value },
        }).unwrap()
        if (response.success) {
            toast.success('Order reassigned successfully.')
            onClose()
            onSubmit?.()
        } else {
            toast.error(response.message || 'Failed to reassign order.')
        }
    }

    const handleAssign = async () => {
        if (!technician) {
            toast.error('Please select a technician.')
            return
        }
        try {
            if (order.assignments?.length) {
                await reassign(technician)
            } else {
                await assign(technician)
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to assign/reassign order.')
            console.error('Error assigning/reassigning order:', error)
        }
    }

    const loadOptionsUsers = async (
        search: string,
        loadedOptions: any,
        { page }: { page: number },
    ) => {
        const res = await getTechnicians(
            `page=${page}&search=${search}`,
        ).unwrap()

        return {
            options:
                res?.data.map(user => ({
                    value: user.id,
                    label: user.fullName,
                })) || [],
            hasMore: page !== res?.pagination?.maxPages,
            additional: {
                page: page + 1,
            },
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                order.assignments?.length ? 'Reassign Order' : 'Assign Order'
            }
        >
            <div className="modal__body">
                <ul className="data-list mb-24">
                    <li className="data-list__item">
                        <span className="data-list__label">Order ID</span>
                        <span className="data-list__value">{order.id}</span>
                    </li>
                    <li className="data-list__item">
                        <span className="data-list__label">Customer</span>
                        <span className="data-list__value">
                            {order.customer?.fullName}
                        </span>
                    </li>
                    <li className="data-list__item">
                        <span className="data-list__label">Service</span>
                        <span className="data-list__value">
                            {order.service?.name}
                        </span>
                    </li>
                    <li className="data-list__item">
                        <span className="data-list__label">Price</span>
                        <span className="data-list__value">
                            {order.service?.price}
                        </span>
                    </li>
                    <li className="data-list__item">
                        <span className="data-list__label">Address</span>
                        <span className="data-list__value">
                            {order?.customer.address
                                ? `${order?.customer.address?.street}, ${order?.customer.address?.city}, ${order?.customer.address?.state} ${order?.customer.address?.postalCode}, ${order?.customer.address?.country}`
                                : '-'}
                        </span>
                    </li>
                    <li className="data-list__item">
                        <span className="data-list__label">Time</span>
                        <span className="data-list__value">
                            {format(new Date(order.startTime), 'hh:mm a')} -{' '}
                            {format(new Date(order.endTime), 'hh:mm a')}
                        </span>
                    </li>
                </ul>
                <SelectBoxAsynk
                    wrapperClassName="mb-24"
                    fetchFunk={loadOptionsUsers}
                    label="Technician"
                    value={technician}
                    defaultValue={
                        order.assignments?.length
                            ? {
                                  value: order.assignments[0].id,
                                  label: order.assignments[0].fullName,
                              }
                            : undefined
                    }
                    onChange={setTechnician}
                />
            </div>
            <div className="modal__footer">
                <Button
                    onClick={onClose}
                    className="btn--secondary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleAssign}
                    className="btn--primary"
                    disabled={isAssigning || isReassigning}
                >
                    {isAssigning || isReassigning ? 'Processing...' : 'Confirm'}
                </Button>
            </div>
        </Modal>
    )
}

export default AssignOrderModal
